/* eslint-disable @typescript-eslint/ban-ts-comment */

import type { RedirectHref } from '@zg-rentals/auth-react/client';
import type { PopupLoginOptions, RedirectLoginOptions } from '@zillow/auth-core';

// @ts-ignore: local package uses declaration files, ignore for external consumers
import { getZillowWebLoginURL } from '@zillow/auth-core/dist/es/zoam-web-sdk/utils/getZillowWebLoginURL';
// @ts-ignore: local package uses declaration files, ignore for external consumers
import zillowWebLogin from '@zillow/auth-core/dist/es/zoam-web-sdk/core/zillowWebLogin';
import type { ZillowLoginBaseOptions } from '.';

export function resolveAuthBaseUrl(raw = '/') {
  if (!raw.startsWith('/') || typeof window === 'undefined') return raw;
  return raw === '/' ? window.location.origin : `${window.location.origin}${raw}`;
}

export function partialUrlEncode(str: string) {
  return str.replaceAll('%', '%25').replaceAll('&', '%26').replaceAll('?', '%3F').replaceAll('#', '%23');
}

export type ZillowRedirectLoginOptions = ZillowLoginBaseOptions &
  Omit<RedirectLoginOptions, 'loginMode' | 'trackingConfig' | 'envConfig' | 'redirectUri'> & {
    /** Base URL for the login flow (defaults to https://www.zillow.com if undefined) */
    baseURL?: string;

    /** Optional: path to redirect to after login (defaults to location.pathname) */
    redirectPath?: string;

    /**
     * Optional: forward prefix, use for local development to support redirecting back to localhost dev server
     * @note only works for serverless apps; SSR apps still need to use `rjs-ext` to login and copy cookies to localhost
     */
    forwardPrefix?: string;
  };

/**
 * Returns a Zillow login URL string for use in client-rendered anchor `href`
 * values.
 *
 * @note `forwardPrefix` only supported for serverless apps
 *
 * @example
 * ```typescript
 * <a href={zillowLoginHref({ entryPoint: 'example-web', baseURL: process.env.NEXT_PUBLIC_AUTH_BASE_URL })}>
 *   Login
 * </a>
 * ```
 */
export function zillowLoginHref(
  { baseURL, disableForceLogin, forwardPrefix, redirectPath, ...options }: ZillowRedirectLoginOptions,
  params = new URLSearchParams(),
): string {
  const resolvedBaseURL = resolveAuthBaseUrl(baseURL);
  const resolvedRedirectPath = redirectPath ?? `${location.pathname}?${params}`;

  return getZillowWebLoginURL({
    ...options,
    baseURL: resolvedBaseURL,
    forceLogin: !disableForceLogin,
    redirectUri: forwardPrefix
      ? `${forwardPrefix}?protocol=https${location.port ? `&port=${location.port}` : ''}&path=${partialUrlEncode(
          resolvedRedirectPath,
        )}`
      : resolvedRedirectPath,
  });
}

/**
 * Returns a `redirectHref` callback for use with `Auth.Boundary` on the
 * client. The callback builds a Zillow login URL from the current page path
 * and auth query params.
 *
 * @note `forwardPrefix` only supported for serverless apps
 *
 * @example
 * ```typescript
 * <Auth.Boundary redirectHref={zillowLoginRedirectHref({ entryPoint: 'example-web', baseURL: process.env.NEXT_PUBLIC_AUTH_BASE_URL })}>
 *   <p>Hello logged in user</p>
 * </Auth.Boundary>
 * ```
 */
export function zillowLoginRedirectHref(options: ZillowRedirectLoginOptions): RedirectHref {
  return (_href: string, params: URLSearchParams) => zillowLoginHref(options, params);
}

/**
 * Triggers a Zillow web login via full-page redirect. Use this for standalone
 * login buttons outside of an `Auth.Boundary`.
 *
 * @note use this function in an `onClick` handler or `useEffect` directly since it produces side-effects
 * @note `forwardPrefix` only supported for serverless apps
 *
 * @example
 * ```typescript
 * <button onClick={() => zillowRedirectLogin({ baseURL: process.env.NEXT_PUBLIC_AUTH_BASE_URL, entryPoint: 'example-web' })}>
 *   Login
 * </button>
 * ```
 *
 * @example
 * ```typescript
 * useEffect(() => {
 *   zillowRedirectLogin({ baseURL: process.env.NEXT_PUBLIC_AUTH_BASE_URL, entryPoint: 'example-web' });
 * }, []);
 * ```
 */
export async function zillowRedirectLogin({
  baseURL,
  disableForceLogin,
  entryPoint,
  forwardPrefix,
  redirectPath,
  ...options
}: ZillowRedirectLoginOptions) {
  const resolvedBaseURL = resolveAuthBaseUrl(baseURL);

  redirectPath ||= location.pathname;

  const redirectUri = forwardPrefix
    ? `${forwardPrefix}?protocol=https${
        typeof window !== 'undefined' && window.location.port ? `&port=${window.location.port}` : ''
      }&path=${partialUrlEncode(redirectPath)}`
    : redirectPath;

  return zillowWebLogin({
    ...options,
    forceLogin: !disableForceLogin,
    envConfig: { baseURLOverride: resolvedBaseURL },
    loginMode: 'redirect',
    redirectUri,
    trackingConfig: { entryPoint },
  });
}

export type ZillowPopupLoginOptions = ZillowLoginBaseOptions &
  Omit<PopupLoginOptions, 'loginMode' | 'trackingConfig' | 'envConfig'> & {
    /** Base URL for the login flow (defaults to https://www.zillow.com if undefined) */
    baseURL?: string;
  };

/**
 * Triggers a Zillow web login using popup mode.
 *
 * @note use this function in an `onClick` handler or `useEffect` directly since it produces side-effects
 * @note due to ZOAM limitations this does not work well in local development; use `zillowRedirectLogin` instead (if possible)
 *
 * @example
 * ```typescript
 * <button onClick={() => zillowPopupLogin({ baseURL: process.env.NEXT_PUBLIC_AUTH_BASE_URL, entryPoint: 'example-web' })}>
 *   Login
 * </button>
 * ```
 *
 * @example
 * ```typescript
 * useEffect(() => {
 *   zillowPopupLogin({ baseURL: process.env.NEXT_PUBLIC_AUTH_BASE_URL, entryPoint: 'example-web' });
 * }, []);
 * ```
 */
export async function zillowPopupLogin({
  baseURL,
  disableForceLogin,
  entryPoint,
  ...options
}: ZillowPopupLoginOptions) {
  const resolvedBaseURL = resolveAuthBaseUrl(baseURL);

  return zillowWebLogin({
    ...options,
    forceLogin: !disableForceLogin,
    envConfig: { baseURLOverride: resolvedBaseURL },
    loginMode: 'popup',
    trackingConfig: { entryPoint },
  });
}
